import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from '../components/Icons.jsx';

const sections = {
  vr: { label: 'Verbal Reasoning', maxRaw: 44 },
  dm: { label: 'Decision Making', maxRaw: 35 },
  qr: { label: 'Quantitative Reasoning', maxRaw: 36 }
};

// Approximate raw → scaled conversion. UCAT consortium doesn't publish an
// official table; this is a Medic Mind heuristic derived from cohort
// distributions. Note: the UCAT cognitive test has THREE scored sections
// (VR, DM, QR) — Abstract Reasoning was removed in 2024. Max total = 2700.
function rawToScaled(raw, maxRaw) {
  const ratio = Math.max(0, Math.min(1, raw / maxRaw));
  const curved = Math.pow(ratio, 0.88);
  return Math.round(300 + curved * 600);
}

// Bands recalibrated for the 3-section UCAT (post-2024). The cognitive total
// mean is ~1870 (down from ~2500 when AR was included), and the 90th
// percentile sits around 2230. Source: UCAT Consortium annual test statistics.
const bands = [
  { min: 2500, max: 2700, label: 'Elite', pct: '~99.5th+', verdict: 'Top of the cohort. Competitive at every UK med + dental school including UCL, Imperial, Cambridge.', colour: 'var(--brand-purple)' },
  { min: 2300, max: 2499, label: 'Outstanding', pct: '~95th–99th', verdict: 'Top 5%. Strongly competitive at UCAT-weighted schools (Bristol, Newcastle, Sheffield, Manchester ranking).', colour: 'var(--brand-purple)' },
  { min: 2200, max: 2299, label: 'Strong', pct: '~90th–95th', verdict: 'Top 10%. Above the threshold at most schools; competitive with strong grades + personal statement.', colour: 'var(--brand-purple-2)' },
  { min: 2100, max: 2199, label: 'Solid', pct: '~80th–90th', verdict: 'Top 20%. Workable at most schools; borderline at the most UCAT-weighted ones.', colour: 'var(--brand-purple-2)' },
  { min: 2000, max: 2099, label: 'Good', pct: '~70th–80th', verdict: 'Comfortably above average. Strong choice of schools when paired with A*AA-style grades.', colour: 'var(--accent-2)' },
  { min: 1900, max: 1999, label: 'Above average', pct: '~55th–70th', verdict: 'Just above the national median (~1870). Apply to schools that use UCAT thresholds rather than rankings.', colour: 'var(--accent-2)' },
  { min: 1800, max: 1899, label: 'Average', pct: '~45th–55th', verdict: 'Right around the median. Lean on grades, work experience, and SJT band; avoid ranking-based UCAT schools.', colour: 'var(--accent-2)' },
  { min: 1700, max: 1799, label: 'Below average', pct: '~30th–45th', verdict: 'Below median. Target schools that weight UCAT lightly (Cardiff, Liverpool, KCL contextual) or use it for interview only.', colour: 'var(--text-muted)' },
  { min: 0, max: 1699, label: 'Low', pct: 'Bottom 30%', verdict: 'A retake or deliberate strategy is worth considering. Many schools have effective thresholds around 1900+.', colour: 'var(--text-muted)' }
];

const sjtBands = [
  { band: 1, pct: 'Top 25%', desc: 'Strong asset. Cardiff, Liverpool prefer Band 1 or 2.' },
  { band: 2, pct: 'Upper-middle', desc: 'Safe at the majority of UK med schools.' },
  { band: 3, pct: 'Lower-middle', desc: 'Many schools accept Band 3; a few weight against it.' },
  { band: 4, pct: 'Bottom 25%', desc: 'Risky. Glasgow will reject on Band 4 alone.' }
];

export default function ScoreConverter() {
  const [mode, setMode] = useState('raw');
  const [vrRaw, setVrRaw] = useState(30);
  const [dmRaw, setDmRaw] = useState(24);
  const [qrRaw, setQrRaw] = useState(26);
  const [vrScaled, setVrScaled] = useState(700);
  const [dmScaled, setDmScaled] = useState(700);
  const [qrScaled, setQrScaled] = useState(700);
  const [sjt, setSjt] = useState(2);

  const scaled = useMemo(() => {
    if (mode === 'raw') {
      return {
        vr: rawToScaled(vrRaw, sections.vr.maxRaw),
        dm: rawToScaled(dmRaw, sections.dm.maxRaw),
        qr: rawToScaled(qrRaw, sections.qr.maxRaw)
      };
    }
    return { vr: vrScaled, dm: dmScaled, qr: qrScaled };
  }, [mode, vrRaw, dmRaw, qrRaw, vrScaled, dmScaled, qrScaled]);

  const total = scaled.vr + scaled.dm + scaled.qr;
  const band = useMemo(() => bands.find((b) => total >= b.min && total <= b.max) || bands[bands.length - 1], [total]);
  const sjtInfo = sjtBands.find((b) => b.band === Number(sjt));

  return (
    <>
      <div className="page-head">
        <h1>Score converter</h1>
        <p className="subtitle">Convert raw marks (number correct) into scaled UCAT scores. Heuristic — UCAT doesn't publish an official table.</p>
      </div>

      <div className="card" style={{ marginBottom: 22 }}>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 18 }}>
          <ModeToggle active={mode === 'raw'} onClick={() => setMode('raw')}>Enter raw marks</ModeToggle>
          <ModeToggle active={mode === 'scaled'} onClick={() => setMode('scaled')}>I already have scaled scores</ModeToggle>
        </div>

        <h3 style={{ margin: '0 0 14px', fontSize: 18 }}>Your scores</h3>
        <div className="r-grid-3">
          {mode === 'raw' ? (
            <>
              <RawInput label="Verbal Reasoning" maxRaw={44} value={vrRaw} onChange={setVrRaw} scaled={scaled.vr} />
              <RawInput label="Decision Making" maxRaw={35} value={dmRaw} onChange={setDmRaw} scaled={scaled.dm} />
              <RawInput label="Quantitative Reasoning" maxRaw={36} value={qrRaw} onChange={setQrRaw} scaled={scaled.qr} />
            </>
          ) : (
            <>
              <ScaledInput label="Verbal Reasoning" value={vrScaled} onChange={setVrScaled} />
              <ScaledInput label="Decision Making" value={dmScaled} onChange={setDmScaled} />
              <ScaledInput label="Quantitative Reasoning" value={qrScaled} onChange={setQrScaled} />
            </>
          )}
        </div>

        <div style={{ marginTop: 18 }}>
          <label style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-2)' }}>SJT band</label>
          <div style={{ display: 'flex', gap: 8, marginTop: 6, flexWrap: 'wrap' }}>
            {[1, 2, 3, 4].map((b) => (
              <button
                key={b} onClick={() => setSjt(b)}
                style={{
                  padding: '8px 14px', borderRadius: 999, cursor: 'pointer',
                  border: `1px solid ${b === sjt ? 'var(--brand-purple)' : 'var(--border)'}`,
                  background: b === sjt ? 'var(--accent-soft)' : '#fff',
                  color: b === sjt ? 'var(--brand-deep)' : 'var(--text-2)',
                  fontWeight: b === sjt ? 600 : 500, fontSize: 14
                }}
              >Band {b}</button>
            ))}
          </div>
        </div>
      </div>

      <div className="card" style={{ marginBottom: 22, borderLeft: `5px solid ${band.colour}` }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: 8 }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 28, fontWeight: 700, letterSpacing: '-0.02em' }}>{total} <span style={{ color: 'var(--text-muted)', fontSize: 18, fontWeight: 500 }}>/ 2700 scaled</span></h2>
            {mode === 'raw' && (
              <p style={{ margin: '4px 0 0', color: 'var(--text-muted)', fontSize: 13.5 }}>
                VR {scaled.vr} · DM {scaled.dm} · QR {scaled.qr}
              </p>
            )}
          </div>
          <span className="pill pill-teal" style={{ fontSize: 13 }}>{band.label} · {band.pct}</span>
        </div>
        <p style={{ margin: '10px 0 0', color: 'var(--text-2)', fontSize: 15 }}>{band.verdict}</p>
        <div style={{ marginTop: 16, padding: 14, background: 'var(--card-soft)', borderRadius: 10, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
          <div>
            <strong>SJT Band {sjt}</strong> · {sjtInfo.pct}
            <p style={{ margin: '2px 0 0', color: 'var(--text-muted)', fontSize: 13 }}>{sjtInfo.desc}</p>
          </div>
          <Link to="/where-can-i-apply" className="btn btn-primary">See which schools <ArrowRight width="16" height="16" /></Link>
        </div>
      </div>

      <h2 style={{ margin: '24px 0 14px', fontSize: 22 }}>Decile bands</h2>
      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <div className="table-scroll">
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: 'var(--card-soft)' }}>
              <th style={cellHead}>Scaled total</th>
              <th style={cellHead}>National percentile</th>
              <th style={cellHead}>Band</th>
              <th style={cellHead}>What it means</th>
            </tr>
          </thead>
          <tbody>
            {bands.map((b) => (
              <tr key={b.label} style={{ borderTop: '1px solid var(--border)', background: total >= b.min && total <= b.max ? 'var(--accent-soft)' : '#fff' }}>
                <td style={cell}>{b.min === 2700 ? '2700' : `${b.min}–${b.max}`}</td>
                <td style={cell}>{b.pct}</td>
                <td style={cell}><span style={{ color: b.colour, fontWeight: 700 }}>{b.label}</span></td>
                <td style={{ ...cell, color: 'var(--text-2)', fontSize: 14 }}>{b.verdict}</td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>

      <p style={{ color: 'var(--text-muted)', fontSize: 13, margin: '16px 0 0' }}>
        Raw-to-scaled conversion is a heuristic. UCAT consortium doesn't publish an official table — exact splits vary year to year. Treat the result as a guide, not gospel.
      </p>

      <section className="parents-cta" style={{ marginTop: 32 }}>
        <div>
          <h2>Below 2500 and aiming for top schools?</h2>
          <p>The gap between 2400 and 2600 is usually unlocked by pacing, not knowledge. Medic Mind tutors specialise in the timing strategies you can't drill alone.</p>
        </div>
        <a className="btn" href="https://www.medicmind.co.uk/contact-us/" target="_blank" rel="noreferrer">Book a free call <ArrowRight width="16" height="16" /></a>
      </section>
    </>
  );
}

function ModeToggle({ active, onClick, children }) {
  return (
    <button onClick={onClick} style={{
      padding: '10px 16px', borderRadius: 99, fontSize: 13.5, cursor: 'pointer',
      border: `1px solid ${active ? 'var(--brand-purple)' : 'var(--border)'}`,
      background: active ? 'var(--brand-purple)' : '#fff',
      color: active ? '#fff' : 'var(--text-2)', fontWeight: 600
    }}>{children}</button>
  );
}

function RawInput({ label, maxRaw, value, onChange, scaled }) {
  return (
    <div>
      <label style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-2)' }}>{label}</label>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginTop: 6 }}>
        <input
          type="number" min="0" max={maxRaw}
          value={value}
          onChange={(e) => onChange(Math.max(0, Math.min(maxRaw, Number(e.target.value) || 0)))}
          style={{ width: 84, padding: '12px 12px', fontSize: 18, fontWeight: 700, border: '1px solid var(--border-strong)', borderRadius: 10, background: '#fff', textAlign: 'right' }}
        />
        <span style={{ fontSize: 14, color: 'var(--text-muted)' }}>/ {maxRaw} correct</span>
      </div>
      <input
        type="range" min="0" max={maxRaw} value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        style={{ width: '100%', marginTop: 8, accentColor: 'var(--brand-purple)' }}
      />
      <div style={{ marginTop: 6, padding: '6px 10px', background: 'var(--accent-soft)', borderRadius: 8, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--brand-purple)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Scaled</span>
        <strong style={{ fontSize: 15, color: 'var(--brand-deep)' }}>{scaled}</strong>
      </div>
    </div>
  );
}

function ScaledInput({ label, value, onChange }) {
  return (
    <div>
      <label style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-2)' }}>{label}</label>
      <input
        type="number" min="300" max="900" step="10"
        value={value}
        onChange={(e) => onChange(Math.max(300, Math.min(900, Number(e.target.value) || 0)))}
        style={{ width: '100%', padding: '12px 14px', fontSize: 18, fontWeight: 600, border: '1px solid var(--border-strong)', borderRadius: 10, marginTop: 6, background: '#fff' }}
      />
      <input
        type="range" min="300" max="900" step="10" value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        style={{ width: '100%', marginTop: 6, accentColor: 'var(--brand-purple)' }}
      />
    </div>
  );
}

const cellHead = { padding: '14px 16px', textAlign: 'left', fontSize: 12, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 700 };
const cell = { padding: '14px 16px', fontSize: 15 };
